import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { sendDemoLeadOtp, verifyDemoLeadOtp } from '../utils/demoLeadOtp'

const otpFieldStyle = {
  '& .MuiOutlinedInput-root': {
    height: 48,
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: 18,
    letterSpacing: '.3em',
    '& input': { textAlign: 'center' },
    '& fieldset': { borderColor: '#C9D6E6', borderRadius: '8px' },
    '&:hover fieldset': { borderColor: '#1677F7' },
    '&.Mui-focused fieldset': { borderColor: '#1677F7', borderWidth: '2px' },
  },
}

export default function DemoPhoneOtp({ mobileNumber, hasPhoneInput, onVerified }) {
  const previousMobileNumber = useRef(mobileNumber)
  const [challengeId, setChallengeId] = useState('')
  const [otp, setOtp] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [resendWait, setResendWait] = useState(0)
  const [verified, setVerified] = useState(false)

  useEffect(() => {
    if (previousMobileNumber.current !== mobileNumber) {
      previousMobileNumber.current = mobileNumber
      setChallengeId('')
      setOtp('')
      setMessage('')
      setError('')
      setResendWait(0)
      setVerified(false)
      onVerified(null)
    }
  }, [mobileNumber, onVerified])

  useEffect(() => {
    if (resendWait <= 0) return undefined
    const timer = setTimeout(() => setResendWait((seconds) => Math.max(0, seconds - 1)), 1000)
    return () => clearTimeout(timer)
  }, [resendWait])

  const isMobileValid = /^\+\d{10,15}$/.test(mobileNumber)

  const handleSendOtp = async () => {
    setIsSending(true)
    setError('')
    setMessage('')
    setVerified(false)
    onVerified(null)
    try {
      const result = await sendDemoLeadOtp(mobileNumber)
      setChallengeId(result.challengeId)
      setOtp('')
      setResendWait(result.resendAfterSeconds || 30)
      setMessage(`We sent a verification code by SMS to ${mobileNumber}.`)
    } catch (sendError) {
      if (sendError.retryAfterSeconds) setResendWait(sendError.retryAfterSeconds)
      setError(sendError.message)
    } finally {
      setIsSending(false)
    }
  }

  const handleVerifyOtp = async () => {
    setIsVerifying(true)
    setError('')
    setMessage('')
    try {
      const result = await verifyDemoLeadOtp(challengeId, otp)
      onVerified(result.verificationId)
      setVerified(true)
      setOtp('')
      setMessage('')
    } catch (verifyError) {
      setError(verifyError.message)
    } finally {
      setIsVerifying(false)
    }
  }

  if (!hasPhoneInput) return null

  if (verified) {
    return (
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.8, px: 1.4, py: 1, borderRadius: 2, bgcolor: '#EDF7EE', color: '#1E6B2D' }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Phone number verified</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 1.25 }}>
      {!challengeId ? (
        <Button
          type="button"
          variant="outlined"
          disabled={!isMobileValid || isSending || resendWait > 0}
          onClick={handleSendOtp}
          sx={{ color: '#1677F7', borderColor: '#1677F7', textTransform: 'none', borderRadius: 2 }}
        >
          {isSending ? 'Sending...' : 'Get OTP'}
        </Button>
      ) : (
        <Box sx={{ p: 1.5, border: '1px solid #D6E4F5', borderRadius: 2, bgcolor: '#F7FAFE' }}>
          <Typography component="label" htmlFor="demo-otp-input" sx={{ display: 'block', color: '#14243A', fontSize: 13, fontWeight: 700, mb: 0.8 }}>
            Enter the 6-digit code
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <TextField
              id="demo-otp-input"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', maxLength: 6, 'aria-label': '6-digit OTP' }}
              sx={{ ...otpFieldStyle, flex: '1 1 160px', minWidth: 160 }}
            />
            <Button
              type="button"
              variant="contained"
              disabled={otp.length !== 6 || isVerifying}
              onClick={handleVerifyOtp}
              sx={{ height: 48, flex: '0 0 auto', px: 3, bgcolor: '#1677F7', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none' }}
            >
              {isVerifying ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              type="button"
              variant="text"
              disabled={isSending || resendWait > 0}
              onClick={handleSendOtp}
              sx={{ height: 48, flex: '0 0 auto', color: '#1677F7', textTransform: 'none', fontWeight: 700 }}
            >
              {isSending ? 'Sending...' : resendWait > 0 ? `Resend in ${resendWait}s` : 'Resend OTP'}
            </Button>
          </Box>
        </Box>
      )}
      {!isMobileValid && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#5B6B80' }}>
          Enter a valid phone number to get an OTP.
        </Typography>
      )}
      {resendWait > 0 && !challengeId && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#5B6B80' }}>
          You can request another code in {resendWait}s.
        </Typography>
      )}
      {message && <Alert severity="success" sx={{ mt: 1 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  )
}
